import { getQueryParam } from "@/react/helpers/urlHelpers";
import { browserSessionStorage } from "@/react/lib/browserStorage";

// The branded Android app appends `external_transaction_token` to the
// checkout URL when a member reaches checkout through the in-app webview
// link-out. Capture it once on checkout-page mount and forward it on every
// create call for the rest of the checkout session so the reporting engine
// can later attribute the charge to Google.
//
// sessionStorage (not component state, not the URL) is required because
// Stripe.js confirmPayment/confirmSetup redirects the browser to a hosted
// page for 3DS/SEPA and returns via `return_url`; useVerifyAttempt rebuilds
// state from that return URL, which never carries the token. Anything held
// only in memory or only in the initial URL is lost on that leg.
const STORAGE_KEY = "paywalls:external_transaction_token";

// Query params Stripe.js appends when returning from a hosted 3DS/SEPA
// redirect. This is the same signal set PaywallCheckoutAttemptsStageHandler
// uses to decide whether to trigger verification on mount, so "is this the
// return leg" stays consistent across both call sites.
const RETURN_LEG_PARAMS = [
  "invoice",
  "payment_intent",
  "redirect_status",
  "setup_intent",
  "setup_intent_id",
];

const isReturnLeg = (): boolean =>
  RETURN_LEG_PARAMS.some(param => Boolean(getQueryParam(param)));

const readTokenFromUrl = (): string | undefined =>
  new URLSearchParams(window.location.search).get(
    "external_transaction_token",
  ) || undefined;

/**
 * Call once per checkout-page mount. URL-authoritative: a fresh URL value is
 * always captured and persisted, overwriting any stale value. Absent that,
 * the sessionStorage fallback is used ONLY on the Stripe redirect/verify
 * return leg (see RETURN_LEG_PARAMS) -- where the token was captured before
 * the redirect but the return URL never carries it. A fresh mount with
 * neither the token nor a return-leg param is a new, unrelated checkout in
 * this tab; any stale value is cleared rather than adopted, so a token from
 * an abandoned/failed checkout can never leak onto an unrelated attempt.
 */
export const captureExternalTransactionToken = (): string | undefined => {
  const urlToken = readTokenFromUrl();
  if (urlToken) {
    browserSessionStorage.setItem(STORAGE_KEY, urlToken);
    return urlToken;
  }

  if (isReturnLeg()) {
    return browserSessionStorage.getItem(STORAGE_KEY) ?? undefined;
  }

  browserSessionStorage.removeItem(STORAGE_KEY);
  return undefined;
};

/**
 * Call when the checkout session reaches a state where the token must not
 * leak into a later, unrelated checkout in the same tab: on successful
 * completion, or on a terminal (non-retryable) failure.
 */
export const clearExternalTransactionToken = (): void => {
  browserSessionStorage.removeItem(STORAGE_KEY);
};
